import React, { useEffect } from 'react'
import { Form, Select, Switch, Input, Alert, Button, message } from 'antd'
import { useI18n } from '@/shared/i18n'
import { useCompanyStyle, useUpdateCompanyStyle } from '@/shared/api/companyApi'

const { Option } = Select

const StyleSettings: React.FC = () => {
  const { t } = useI18n('modules')
  const [form] = Form.useForm()
  const { data: styleData, isLoading } = useCompanyStyle()
  const updateMutation = useUpdateCompanyStyle()

  useEffect(() => {
    if (styleData?.style_config) {
      form.setFieldsValue({ style: styleData.style_config })
    }
  }, [styleData, form])

  const handleSave = async () => {
    const values = await form.validateFields()
    const styleConfig = values.style || {}
    updateMutation.mutate(styleConfig, {
      onSuccess: () => {
        message.success(t('settings.messages.saveSuccess'))
      },
      onError: () => {
        message.error(t('settings.messages.saveFailed'))
      },
    })
  }

  const formalityOptions = [
    t('settings.options.style.formalityLevel1'),
    t('settings.options.style.formalityLevel2'),
    t('settings.options.style.formalityLevel3'),
    t('settings.options.style.formalityLevel4'),
    t('settings.options.style.formalityLevel5'),
  ]

  const politenessOptions = [
    t('settings.options.style.politenessLevel1'),
    t('settings.options.style.politenessLevel2'),
    t('settings.options.style.politenessLevel3'),
    t('settings.options.style.politenessLevel4'),
    t('settings.options.style.politenessLevel5'),
  ]

  const enthusiasmOptions = [
    t('settings.options.style.enthusiasmLevel1'),
    t('settings.options.style.enthusiasmLevel2'),
    t('settings.options.style.enthusiasmLevel3'),
    t('settings.options.style.enthusiasmLevel4'),
    t('settings.options.style.enthusiasmLevel5'),
  ]

  return (
    <Form form={form} layout="vertical" size="middle">
      <Alert
        message={t('settings.sections.brandPersona')}
        description={t('settings.help.responsePreferences')}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form.Item
        label={t('settings.labels.persona')}
        name={['style', 'persona']}
        help={t('settings.help.persona')}
      >
        <Input.TextArea
          placeholder={t('settings.placeholders.persona')}
          rows={2}
          maxLength={200}
        />
      </Form.Item>

      <Form.Item
        label={t('settings.labels.formalityLevel')}
        name={['style', 'base_tone', 'formality_level']}
        help={t('settings.help.formalityLevel')}
      >
        <Select>
          {formalityOptions.map((option, index) => (
            <Option key={index + 1} value={option}>
              {option} ({index + 1}/5)
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={t('settings.labels.politenessLevel')}
        name={['style', 'base_tone', 'politeness_level']}
        help={t('settings.help.politenessLevel')}
      >
        <Select>
          {politenessOptions.map((option, index) => (
            <Option key={index + 1} value={option}>
              {option} ({index + 1}/5)
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={t('settings.labels.enthusiasmLevel')}
        name={['style', 'base_tone', 'enthusiasm_level']}
        help={t('settings.help.enthusiasmLevel')}
      >
        <Select>
          {enthusiasmOptions.map((option, index) => (
            <Option key={index + 1} value={option}>
              {option} ({index + 1}/5)
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        label={t('settings.labels.emojiPolicy')}
        name={['style', 'emoji_policy']}
        help={t('settings.help.emojiPolicy')}
      >
        <Select>
          <Option value="none">{t('settings.options.style.emojiPolicyNone')}</Option>
          <Option value="minimal">{t('settings.options.style.emojiPolicyMinimal')}</Option>
          <Option value="moderate">{t('settings.options.style.emojiPolicyModerate')}</Option>
          <Option value="friendly">{t('settings.options.style.emojiPolicyFriendly')}</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={t('settings.labels.useEmphasis')}
        name={['style', 'response_preferences', 'use_emphasis']}
        valuePropName="checked"
        help={t('settings.help.useEmphasis')}
      >
        <Switch />
      </Form.Item>

      <Form.Item
        label={t('settings.labels.lineBreaks')}
        name={['style', 'response_preferences', 'line_breaks']}
        valuePropName="checked"
        help={t('settings.help.useLineBreaks')}
      >
        <Switch />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          onClick={handleSave}
          loading={updateMutation.isPending}
        >
          {t('common.save')}
        </Button>
      </Form.Item>
    </Form>
  )
}

export default StyleSettings
